// @ts-nocheck
import { useState, useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase, IconFileDescription, IconFileInvoice, IconCalendar,
  IconChevronRight, IconCoins, IconClock,
} from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import Time from '@/utils/time';
import Helper from '@/utils/helper';
import PageContext from '@/utils/page-context';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import * as Enums from '@/utils/enums';
import { quoteStatusDisplay } from '../CustomerZoneShared';
import styles from '../CustomerZoneDetail.module.css';

const TABS = [
  { key: 'jobs',         label: 'Jobs',         Icon: IconBriefcase },
  { key: 'quotes',       label: 'Quotes',       Icon: IconFileDescription },
  { key: 'invoices',     label: 'Invoices',     Icon: IconFileInvoice },
  { key: 'appointments', label: 'Appointments', Icon: IconCalendar },
];

const LIST_PAGES: Record<string, string> = { jobs: 'joblist', quotes: 'quotelist', invoices: 'invoicelist', appointments: 'appointmentlist' };

function Pill({ text, tone }: { text: string; tone: string }) {
  const cls = { ok: styles.pillOk, warn: styles.pillWarn, danger: styles.pillDanger, info: styles.pillInfo }[tone] || styles.pillInfo;
  return <span className={`${styles.pill} ${cls}`}>{text}</span>;
}

function QuoteCard({ quote, currencySymbol, href, onClick }: any) {
  const { label, tone } = quoteStatusDisplay(quote.QuoteStatus);
  return (
    <a className={styles.miniCard} href={href} onClick={(e) => {
      if (e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      onClick();
    }}>
      <div className={styles.miniHead}>
        <span className={styles.miniTitle}>{quote.QuoteNumber}</span>
        <Pill text={label} tone={tone} />
      </div>
      <div className={styles.miniRows}>
        {quote.ExpiryDate && (
          <div className={styles.miniRow}>
            <span className={styles.miniRowIcon}><IconClock size={13} /></span>
            <span>{Time.toISOString(Time.parseDate(quote.ExpiryDate), false, false)}</span>
          </div>
        )}
        {quote.TotalInclusive != null && (
          <div className={styles.miniRow}>
            <span className={styles.miniRowIcon}><IconCoins size={13} /></span>
            <span><b>{Helper.getCurrencyValue(quote.TotalInclusive, currencySymbol)}</b></span>
          </div>
        )}
      </div>
    </a>
  );
}

function Group({ label, tone, quotes, currencySymbol, getHref, onCard }: any) {
  if (!quotes.length) return null;
  const cls = { success: styles.groupLabelSuccess, ok: styles.groupLabelOk, warn: styles.groupLabelWarn, danger: styles.groupLabelDanger, info: styles.groupLabelInfo }[tone] || styles.groupLabelInfo;
  return (
    <div className={styles.listGroup}>
      <div className={`${styles.groupLabel} ${cls}`}>
        {label}<span className={styles.gCount}>({quotes.length})</span>
      </div>
      <div className={styles.grid}>
        {quotes.map((q: any, i: number) => (
          <QuoteCard key={i} quote={q} currencySymbol={currencySymbol} href={getHref(q)} onClick={() => onCard(q)} />
        ))}
      </div>
    </div>
  );
}

export default function CustomerZoneQuoteListPage() {
  const router = useRouter();
  const { t, c } = router.query;
  const pageContext = useContext(PageContext) as any;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<any[]>([]);

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  const { data: quotesData, isFetching } = useQuery(
    ['czQuoteList', t, c, page],
    () => Fetch.post({ url: '/CustomerZone/GetQuotes', params: { pageSize: 50, pageIndex: page, PopulatedList: false, SortExpression: 'QuoteDate', SortDirection: 'descending', QuoteStatusIDList: ['Approved', 'Accepted', 'Declined', 'Invoiced', 'Expired', 'Cancelled'], IncludeClosed: true }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant, keepPreviousData: true }
  );

  useEffect(() => {
    if (!quotesData?.Results) return;
    if (page === 0) setAccumulated(quotesData.Results);
    else setAccumulated(prev => [...prev, ...quotesData.Results]);
  }, [quotesData]);

  useEffect(() => {
    const name = quotesData?.headerTenantName || tenant?.Name;
    if (name) pageContext?.updateTenantName?.(name);
  }, [quotesData, tenant]);

  if (!tenant) return null;

  const currencySymbol = company?.Currency?.Symbol || '';
  const base = `?t=${t}&c=${c}`;
  const logoUrl = company?.LogoUrl || null;
  const companyName = company?.Name || tenant?.Name || '';

  const allQuotes = accumulated;
  const hasMore = (quotesData?.Results?.length ?? 0) >= 50;
  const isEmpty = allQuotes.length === 0 && !!quotesData;

  const awaiting = allQuotes.filter((q: any) => q.QuoteStatus === Enums.QuoteStatus.Approved);
  const accepted = allQuotes.filter((q: any) => q.QuoteStatus === Enums.QuoteStatus.Accepted);
  const declined = allQuotes.filter((q: any) => q.QuoteStatus === Enums.QuoteStatus.Declined);
  const other    = allQuotes.filter((q: any) => ![Enums.QuoteStatus.Approved, Enums.QuoteStatus.Accepted, Enums.QuoteStatus.Declined].includes(q.QuoteStatus));

  const navigate = (tab: string) => router.push(`/customerzone/${LIST_PAGES[tab]}${base}`);
  const goToDetail = (q: any) => router.push(`/customerzone/viewquote${base}&i=${q.ID}`);
  const getHref = (q: any) => `/customerzone/viewquote${base}&i=${q.ID}`;

  return (
    <>
      <div className={styles.navWrap}>
        <header className={styles.nav}>
          <div className={styles.navInner}>
            <a className={styles.navLogo} href={`/customerzone${base}`}>
              <img src="/logo-type-white.svg" alt="ServCraft" onError={(e: any) => { e.target.style.display = 'none'; }} />
            </a>
            <div className={styles.navRight}>
              <a className={styles.btnTrial} href="https://servcraft.co.za/sign-up" target="_blank" rel="noopener">
                Start your free trial
              </a>
            </div>
          </div>
        </header>
      </div>
      <div className={styles.root}>
        <div className={styles.page}>
          <div className={styles.wrap}>
            <div className={styles.portalId}>
              <div className={`${styles.portalBadge} ${!logoUrl ? styles.isFallback : ''}`}>
                {logoUrl ? (
                  <img src={logoUrl} alt={companyName} />
                ) : (
                  <span className={styles.portalBadgeFallback}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h6" />
                    </svg>
                  </span>
                )}
              </div>
              <p className={styles.portalName}>{companyName}</p>
            </div>
          <div className={styles.card}>
            <div className={styles.switchWrap}>
              <div className={styles.cardToolbar}>
                <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                  <Link to="/customerzone" search={{ t, c }}>Portal</Link>
                  <span className={styles.bcSep}><IconChevronRight /></span>
                  <span className={styles.bcCurrent}>Quotes</span>
                </nav>
              </div>
              <div className={styles.switchTabs}>
                {TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    className={`${styles.switchTab}${key === 'quotes' ? ` ${styles.switchTabActive}` : ''}`}
                    onClick={() => navigate(key)}
                  >
                    <Icon size={16} /><span>{label}</span>
                  </button>
                ))}
              </div>
              <div className={`${styles.switchSelect}${mobileOpen ? ` ${styles.switchSelectOpen}` : ''}`}>
                <button className={styles.switchTrigger} onClick={() => setMobileOpen(o => !o)} type="button">
                  <span className={styles.switchTriggerLeft}>
                    <IconFileDescription size={15} />
                    <span>Viewing</span>
                    <span className={styles.switchTriggerCurrent}>Quotes</span>
                  </span>
                  <IconChevronRight className={styles.switchTriggerChev} size={14} />
                </button>
                <div className={styles.switchMenu}>
                  {TABS.map(({ key, label, Icon }) => (
                    <button key={key} className={`${styles.switchOpt}${key === 'quotes' ? ` ${styles.switchOptActive}` : ''}`} onClick={() => { setMobileOpen(false); navigate(key); }}>
                      <Icon size={17} /><span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.cardInner}>
              <h2 className={styles.contentHead}>Your Quotes</h2>
              {isEmpty ? (
                <p className={styles.emptyNote}>There are no quotes</p>
              ) : (
                <div className={styles.listGroups}>
                  <Group label="Awaiting Your Response" tone="warn" quotes={awaiting} currencySymbol={currencySymbol} getHref={getHref} onCard={goToDetail} />
                  <Group label="Accepted" tone="success" quotes={accepted} currencySymbol={currencySymbol} getHref={getHref} onCard={goToDetail} />
                  <Group label="Declined" tone="danger" quotes={declined} currencySymbol={currencySymbol} getHref={getHref} onCard={goToDetail} />
                  <Group label="Other" tone="info" quotes={other} currencySymbol={currencySymbol} getHref={getHref} onCard={goToDetail} />
                  {hasMore && (
                    <div className={styles.loadMore}>
                      <button className={styles.loadMoreBtn} onClick={() => setPage(p => p + 1)} disabled={isFetching}>
                        {isFetching ? 'Loading…' : 'Load more'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.support}>
            <span className={styles.sTitle}>Need support?</span>
            <p>Contact us at <a href="mailto:support@servcraft.co.za">support@servcraft.co.za</a></p>
          </div>
          </div>{/* wrap */}
        </div>{/* page */}
      </div>{/* root */}
    </>
  );
}
